import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Zap, LayoutDashboard, Wallet, FileText, Layers, Trophy, Shield,
  Code, Share2, Briefcase, LogOut, ChevronLeft, ChevronRight,
  Menu, X, DollarSign, ShieldAlert, BookOpen,
  Award, Package, BarChart3, Server, TrendingUp, FlaskConical, Cpu
} from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import SettingsSelector from './SettingsSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BACKEND_URL, withCredentials: true, adapter: 'xhr' });

const NAV_ITEMS = [
  { path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard, section: 'main' },
  { path: '/wallet', labelKey: 'nav.wallet', icon: Wallet, section: 'main', highlight: true },
  { path: '/report', labelKey: 'nav.report', icon: FileText, section: 'main' },
  { path: '/risk', labelKey: 'nav.risk', icon: ShieldAlert, section: 'main' },
  { path: '/journal', labelKey: 'nav.tradeJournal', icon: BookOpen, section: 'main' },
  { path: '/tca', labelKey: 'nav.tcaAnalytics', icon: BarChart3, section: 'main' },
  { path: '/rewards', labelKey: 'nav.rewards', icon: Award, section: 'main' },
  { path: '/quant-lab', labelKey: 'nav.quantLab', icon: FlaskConical, section: 'markets' },
  { path: '/compute', labelKey: 'nav.compute', icon: Cpu, section: 'markets' },
  { path: '/intangi', labelKey: 'nav.intangiTrade', icon: Layers, section: 'markets' },
  { path: '/profiles', labelKey: 'nav.profiles', icon: Briefcase, section: 'markets' },
  { path: '/competition', labelKey: 'nav.arena', icon: Trophy, section: 'community' },
  { path: '/leaderboard', labelKey: 'nav.leaderboard', icon: TrendingUp, section: 'community' },
  { path: '/promoter', labelKey: 'nav.promoter', icon: Share2, section: 'community' },
  { path: '/developers', labelKey: 'nav.apiDocs', icon: Code, section: 'tools' },
  { path: '/sdk', labelKey: 'nav.sdkMcp', icon: Package, section: 'tools' },
  { path: '/infrastructure', labelKey: 'nav.infra', icon: Server, section: 'tools' },
  { path: '/admin', labelKey: 'nav.admin', icon: Shield, section: 'tools', adminOnly: true },
];

const SECTION_KEYS = {
  main: 'nav.principal',
  markets: 'nav.markets',
  community: 'nav.community',
  tools: 'nav.tools',
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { formatCurrency } = useCurrency();
  const { t } = useTranslation();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const [evolution, setEvolution] = useState([]);
  const [pnlPct, setPnlPct] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/api/wallet/balance'),
      api.get('/api/wallet/portfolio-summary'),
    ]).then(([w, p]) => {
      setWalletBalance(w.data?.balance ?? 0);
      const evo = p.data?.evolution || [];
      setEvolution(evo);
      setPnlPct(p.data?.overall_return_pct ?? 0);
    }).catch(() => {});
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;
  const isAdmin = user?.role === 'admin';

  const filteredItems = NAV_ITEMS.filter(item => !item.adminOnly || isAdmin);
  const groupedItems = {};
  filteredItems.forEach(item => {
    if (!groupedItems[item.section]) groupedItems[item.section] = [];
    groupedItems[item.section].push(item);
  });

  const handleLogout = async () => { await logout(); };

  const SidebarContent = () => (
    <div className="flex flex-col h-full" data-testid="sidebar">
      {/* Logo */}
      <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
          <div className="w-8 h-8 bg-white flex items-center justify-center flex-shrink-0">
            <Zap className="w-5 h-5 text-[#030508]" />
          </div>
          {!collapsed && (
            <span className="text-lg font-black tracking-tight text-white" style={{ fontFamily: 'Chivo' }}>
              BlackOre<span className="text-emerald-500">X</span>
            </span>
          )}
        </Link>
        <button onClick={() => setCollapsed(!collapsed)}
          className="text-slate-500 hover:text-white transition-colors hidden lg:block"
          data-testid="sidebar-collapse-btn">
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Wallet Balance Card */}
      {!collapsed && (
        <div className="mx-3 mt-4 mb-2">
          <Link to="/wallet" data-testid="sidebar-wallet-card"
            className="block bg-emerald-500/10 border border-emerald-500/20 p-3 hover:border-emerald-500/40 transition-all group">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-[0.15em] text-emerald-400/70 font-semibold">{t('nav.balance')}</span>
              {pnlPct !== null && (
                <span className={`text-[10px] font-mono font-bold ${pnlPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%
                </span>
              )}
            </div>
            <div className="text-xl font-black text-emerald-400 font-mono" data-testid="sidebar-balance">
              {walletBalance !== null ? formatCurrency(walletBalance) : '---'}
            </div>
            {evolution.length > 1 && (
              <div className="h-[36px] mt-2 -mx-1" data-testid="sidebar-sparkline">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={evolution}>
                    <defs>
                      <linearGradient id="sidebarSparkGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="balance" stroke="#10b981" fill="url(#sidebarSparkGrad)"
                      strokeWidth={1.5} dot={false} isAnimationActive={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </Link>
        </div>
      )}

      {/* Quick Deposit Button */}
      {!collapsed && walletBalance !== null && walletBalance <= 0 && (
        <div className="mx-3 mb-3">
          <Link to="/wallet"
            className="flex items-center justify-center gap-2 w-full py-2.5 bg-emerald-500 text-[#030508] font-bold text-xs tracking-wider hover:bg-emerald-400 transition-colors shadow-[0_0_15px_rgba(16,185,129,0.3)]"
            data-testid="sidebar-deposit-btn">
            <DollarSign className="w-4 h-4" /> {t('nav.depositFunds')}
          </Link>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-4">
        {Object.entries(groupedItems).map(([section, items]) => (
          <div key={section}>
            {!collapsed && (
              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-semibold px-2 mb-1.5">
                {t(SECTION_KEYS[section])}
              </p>
            )}
            <div className="space-y-0.5">
              {items.map(item => {
                const active = isActive(item.path);
                return (
                  <Link key={item.path} to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-all group ${
                      active
                        ? 'bg-emerald-500/15 text-emerald-400 border-l-2 border-emerald-500'
                        : 'text-slate-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent'
                    } ${collapsed ? 'justify-center' : ''}`}
                    data-testid={`sidebar-nav-${item.path.replace('/', '')}`}
                    title={collapsed ? t(item.labelKey) : undefined}
                  >
                    <item.icon className={`w-[18px] h-[18px] flex-shrink-0 ${active ? 'text-emerald-400' : 'text-slate-500 group-hover:text-slate-300'}`} />
                    {!collapsed && <span className="font-medium">{t(item.labelKey)}</span>}
                    {!collapsed && item.highlight && walletBalance !== null && walletBalance > 0 && (
                      <span className="ml-auto text-[10px] font-mono text-emerald-500 bg-emerald-500/10 px-1.5 py-0.5">
                        {formatCurrency(walletBalance).split('.')[0]}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Settings: Language + Currency */}
      <SettingsSelector collapsed={collapsed} />

      {/* User + Logout */}
      <div className="border-t border-white/[0.08] p-3">
        {!collapsed ? (
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm text-white font-medium truncate">{user?.name || user?.email?.split('@')[0]}</p>
              <p className="text-[10px] text-slate-500 font-mono truncate">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="text-slate-500 hover:text-red-400 transition-colors p-1.5" data-testid="sidebar-logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button onClick={handleLogout} className="w-full flex justify-center text-slate-500 hover:text-red-400 transition-colors p-1.5" data-testid="sidebar-logout-btn">
            <LogOut className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed top-3 left-3 z-50 lg:hidden bg-[#0B1015] border border-white/10 p-2 text-white"
        data-testid="sidebar-mobile-toggle"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-[#0B1015] border-r border-white/[0.08]">
            <button onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            <SidebarContent />
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col fixed left-0 top-0 bottom-0 bg-[#0B1015] border-r border-white/[0.08] z-40 transition-all duration-200 ${
        collapsed ? 'w-16' : 'w-60'
      }`}>
        <SidebarContent />
      </aside>
    </>
  );
}
